import { z } from 'zod';

// Types
import { EmployeeTrainingStatus } from '@/types/employee';

// Constants
import { VALIDATION_CONSTRAINTS } from '@/constants/validation';
import { VALIDATION_MESSAGES } from '@/constants/messages';

// Utils
import { isValidAvatarUrl } from '@/utils/url';

const employeeInfoSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string({
        required_error: VALIDATION_MESSAGES.employee.name.required,
      })
      .min(VALIDATION_CONSTRAINTS.name.minLength, {
        message: VALIDATION_MESSAGES.employee.name.tooShort,
      })
      .max(VALIDATION_CONSTRAINTS.name.maxLength, {
        message: VALIDATION_MESSAGES.employee.name.tooLong,
      })
      .trim()
      .optional(),
    avatar: z
      .string()
      .refine(isValidAvatarUrl, {
        message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
      })
      .optional(),
    onboardedAt: z.string().optional(),
    title: z
      .string({
        required_error: VALIDATION_MESSAGES.employee.title.required,
      })
      .min(VALIDATION_CONSTRAINTS.title.minLength, {
        message: VALIDATION_MESSAGES.employee.title.tooShort,
      })
      .max(VALIDATION_CONSTRAINTS.title.maxLength, {
        message: VALIDATION_MESSAGES.employee.title.tooLong,
      })
      .trim()
      .optional(),
  })
  .optional();

// const employeeEducationSchema = z.object({
//   id: z.string().optional(),
//   school: z.string().optional(),
//   address: z.string().optional(),
//   degree: z.string().optional(),
//   major: z.string().optional(),
//   admission: z.string().optional(),
//   graduation: z.string().optional(),
// });

// const employeeSkillSchema = z.object({
//   name: z.string().optional(),
//   items: z.array(z.string()).optional(),
// });

export const employeeCertificationSchema = z.object({
  name: z.string().optional(),
  org: z.string().optional(),
  issue: z.string().optional(),
  url: z.string().optional(),
});

const employeeSchema = z
  .object({
    info: employeeInfoSchema,
    certs: z.array(employeeCertificationSchema).optional(),
  })
  .optional();

const projectSchema = z.object({
  id: z.string().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  joinedAt: z.string().optional(),
  leftAt: z.string().optional(),
  role: z.string().optional(),
});

const projectFeedbackSchema = z.object({
  id: z.string().optional(),
  role: z.string().optional(),
  feedbackHighlight: z.string().optional(),
  related: z.string().optional(),
  relatedName: z.string().optional(),
  relatedAvatar: z
    .string()
    .refine(isValidAvatarUrl, {
      message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
    })
    .optional(),
  projectId: z.string().optional(),
  projectCode: z.string().optional(),
});

export const trainingFeedbackSchema = z.object({
  id: z.string().optional(),
  role: z.string().optional(),
  feedbackHighlight: z.string().optional(),
  related: z.string().optional(),
  relatedName: z.string().optional(),
  relatedAvatar: z
    .string()
    .refine(isValidAvatarUrl, {
      message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
    })
    .optional(),
  trainingId: z.string().optional(),
  topicLabel: z.string().optional(),
});

export const employeeSupporterSchema = z.object({
  name: z.string().optional(),
  avatar: z
    .string()
    .refine(isValidAvatarUrl, {
      message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
    })
    .optional(),
  email: z.string().optional(),
});

export const employeeTrainingSchema = z.object({
  id: z.string(),
  topicLabel: z.string(),
  supporters: z.array(employeeSupporterSchema),
  start: z.string().optional(),
  actualEnd: z.string().optional(),
  status: z.nativeEnum(EmployeeTrainingStatus).optional(),
});

export const menteeSchema = z.object({
  email: z.string().optional(),
  username: z.string().optional(),
  name: z.string().optional(),
  avatar: z
    .string()
    .refine(isValidAvatarUrl, {
      message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
    })
    .optional(),
  onboardedAt: z.string().optional(),
});

const traineeTrainingSchema = z.object({
  id: z.string().optional(),
  topic: z.string().optional(),
  topicLabel: z.string().optional(),
  start: z.string().optional(),
});

export const supporteesSchema = z.object({
  email: z.string().optional(),
  username: z.string().optional(),
  name: z.string().optional(),
  avatar: z
    .string()
    .refine(isValidAvatarUrl, {
      message: VALIDATION_MESSAGES.employee.avatar.invalidUrl,
    })
    .optional(),
  trainings: z.array(traineeTrainingSchema).optional(),
});

/** Schema for Employee Year In Review validation */
export const employeeYearInReviewSchema = z.object({
  id: z.string().optional(),
  year: z
    .number({
      required_error: VALIDATION_MESSAGES.year.required,
      invalid_type_error: VALIDATION_MESSAGES.year.invalid,
    })
    .min(VALIDATION_CONSTRAINTS.year.min, {
      message: VALIDATION_MESSAGES.year.outOfRange,
    })
    .max(VALIDATION_CONSTRAINTS.year.max, {
      message: VALIDATION_MESSAGES.year.outOfRange,
    })
    .optional(),
  openingSentence: z.string().optional(),
  employee: employeeSchema,
  monthsOnProject: z.number().optional(),
  projects: z.array(projectSchema).optional(),
  projectFeedbacks: z.array(projectFeedbackSchema).optional(),
  trainings: z.array(employeeTrainingSchema).optional(),
  trainingFeedbacks: z.array(trainingFeedbackSchema).optional(),
  monthsOnTraining: z.number().optional(),
  mentees: z.array(menteeSchema).optional(),
  supportees: z.array(supporteesSchema).optional(),
  daysOff: z.number().optional(),
});
